import { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Outlet } from 'react-router-dom-v5-compat';

import { Button } from '~/eds/src/components/Button/Button';
import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { PageLayout } from '~/eds/src/modules/PageLayout/PageLayout';
import { Table } from '~/eds/src/modules/Table/Table';
import { Ticket } from '~/features/workflows-clm-evolution/types';
import { getTicketStateLabel } from '~/features/workflows-clm-evolution/utils/getTicketStateLabel';
import { api } from '~/redux/api/createApi';
import { RoutePathType } from '~/routing/enums';
import { useRouting } from '~/routing/useRouting';

const messages = defineMessages({
  pageTitle: {
    id: 'workflowCLMEvolutionTickets.pageTitle',
    defaultMessage: 'Tickets',
    description: 'Page title for workflow CLM evolution tickets page',
  },
  newTicketButton: {
    id: 'workflowCLMEvolutionTickets.newTicketButton',
    defaultMessage: 'New Ticket',
    description: 'Button text to create a new ticket',
  },
  name: {
    id: 'workflowCLMEvolutionTickets.name',
    defaultMessage: 'Name',
    description: 'Name of the ticket',
  },
  state: {
    id: 'workflowCLMEvolutionTickets.state',
    defaultMessage: 'State',
    description: 'Active state of the ticket',
  },
  createdAt: {
    id: 'workflowCLMEvolutionTickets.createdAt',
    defaultMessage: 'Created',
    description: 'Created at date of the ticket',
  },
});

const WorkflowCLMEvolutionTicketsPage = () => {
  const intl = useIntl();
  const { navigate } = useRouting();
  const { data, isLoading } = api.endpoints.listTicketsK2.useQuery({});

  const columns = useMemo(
    () => [
      {
        key: 'name',
        title: intl.formatMessage(messages.name),
        cellType: 'text',
        mapCellProps: (ticket: Ticket) => ({
          text: ticket.name,
        }),
      },
      {
        key: 'state',
        title: intl.formatMessage(messages.state),
        cellType: 'text',
        mapCellProps: (ticket: Ticket) => ({
          text: getTicketStateLabel(ticket.state),
        }),
      },
      {
        key: 'createdAt',
        title: intl.formatMessage(messages.createdAt),
        cellType: 'datetime',
        mapCellProps: (ticket: Ticket) => ({
          datetime: ticket.created_at,
          format: 'short_datetime',
        }),
      },
    ],
    [intl],
  );

  return (
    <PageLayout
      title={intl.formatMessage(messages.pageTitle)}
      headerCalloutContent={
        <Button
          text={intl.formatMessage(messages.newTicketButton)}
          variant="primary"
          onClick={() => {
            navigate(`${RoutePathType.WorkflowCLMEvolutionTickets}/new`);
          }}
        />
      }
    >
      <ContentContainer loadingContent={{ isLoading }}>
        <Table
          name="tickets"
          columns={columns}
          data={data?.data ?? []}
          isLoading={isLoading}
          rowDetails={{
            onClick: (ticket: Ticket) => {
              navigate(
                RoutePathType.WorkflowCLMEvolutionTicketsDetail.replace(
                  ':ticketId',
                  ticket.id,
                ),
              );
            },
          }}
        />
      </ContentContainer>
      <Outlet />
    </PageLayout>
  );
};

export default WorkflowCLMEvolutionTicketsPage;
